import React from 'react';
import { useController, FieldValues, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { classNames } from 'primereact/utils';
import { Dropdown, DropdownItem } from 'components/_new/Dropdown/Dropdown';
import CustomLabel from './Label';

type ValidateDropdownProps<T extends FieldValues> = UseControllerProps<T> & {
	options?: DropdownItem[];
	label?: string;
	placeholder?: string;
	required?: boolean;
	value?: string | boolean | number | null;
	trueFalseSelector?: boolean;
	disabled?: boolean;
	className?: string;
};

const ValidateDropdown = <T extends FieldValues>(props: ValidateDropdownProps<T>) => {
	const {
		name,
		value,
		label,
		placeholder,
		options,
		control,
		trueFalseSelector,
		required = false,
		rules,
		disabled = false,
		className,
	} = props;

	const { t } = useTranslation();

	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		control,
		rules: {
			required: required ? t('misc.forms.fieldRequired', { field: placeholder || label || name }) : false,
			validate: (answer) => (answer ?? value) !== undefined,
			...rules,
		},
	});

	const dropdownOptions: DropdownItem[] = trueFalseSelector
		? [
				{ label: t('misc.yes'), value: true },
				{ label: t('misc.no'), value: false },
		  ]
		: options || [];

	return (
		<div className={className}>
			{label && <CustomLabel name={name} label={label} required={required} />}

			<Dropdown
				ref={field.ref}
				value={field.value ?? value}
				options={dropdownOptions}
				placeholder={placeholder || t('misc.choose')}
				onChange={(e) => field.onChange(e.value)}
				disabled={disabled}
				className={classNames('mt-1', {
					'input-invalid': error,
				})}
				aria-label={label || placeholder}
			/>

			{error && <small className="p-error font-bold ml-3">{error.message}</small>}
		</div>
	);
};

export default ValidateDropdown;
